import './ParcelModalWindow.css';
import cn from 'classnames';
import Column from '@components/Grid/Column.tsx';
import CopyTextToClipboard from '@components/CopyTextToClipboard';
import ParcelModalTimeline from '@components/ParcelModalTimeline';
import PriceIcon from '@assets/icons/diamond-grey-18.svg';
import ReturnIcon from '@assets/icons/return.svg';
import RocketIcon from '@assets/icons/rocket.svg';
import Row from '@components/Grid/Row.tsx';
import Table from '@components/Table/Table.tsx';
import ToggleButtons from '@components/ToggleButtons';
import { createSumFromObject, formatSumFromObject, formatNumber } from '@utils/formatValues';
import { ErrorResponse } from '@api/Responses/ErrorResponse';
import { getParcelImages, getParcel, Parcel, ParcelItem } from '@api/apiClient';
import { Loader } from '@components/Loader';
import { TableColumn, TableColumnType, TableRowType } from '@components/Table/TableTypes';
import { Text, TextColor, TextSize, TextType } from '@components/Text';
import { useQuery } from 'react-query';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface ParcelModalWindowProps {
  parcelId: string;
  returnType?: string;
  isProblematic?: boolean;
}

const getTracking = (statusView: string, returnType?: string, isProblematic?: boolean) => {
  if (statusView !== "return") return "direct"
  if (isProblematic && returnType === 'DeliverToRussia') return "returnProblematicToRussia"
  if (isProblematic) return "returnProblematic";
  if (returnType === 'DeliverToRussia') return "returnToRussia"
  return "return"
}

const ParcelModalWindow = ({ parcelId, returnType, isProblematic }: ParcelModalWindowProps) => {
  const { t } = useTranslation();
  const query = useQuery<Parcel, ErrorResponse>({
    queryKey: ["parcel", parcelId],
    queryFn: () => getParcel(parcelId),
    retry: 1,
  });
  const isReturn = Boolean(
    query.data?.trackEvents
    && query.data.trackEvents.find((e) => (+e.type === 6000 || +e.type === 6001 || +e.type >= 20000))
  );
  const imagesQuery = useQuery({
    queryKey: ["parcelImages", parcelId],
    queryFn: () => getParcelImages(parcelId),
    retry: 1,
    enabled: isReturn,
  });

  const [statusView, setStatusView] = useState<string>(
    isReturn ? "return" : "send",
  );

  useEffect(() => {
    setStatusView(isReturn ? "return" : "send")
  }, [isReturn])

  const columns: TableColumn<ParcelItem>[] = [
    {
      header: t("parcelModalWindowAttachmentsTable.article"),
      accessor: (row: TableRowType<ParcelItem>) => (
          <Text
              color={TextColor.PRIMARY}
              size={TextSize.S}
          >
            {row.data.vendorCode}
          </Text>
      ),
      fixWidth: true,
      noOverflow: true,
      width: "85px"
    },
    {
      header: t("parcelModalWindowAttachmentsTable.name"),
      accessor: (row: TableRowType<ParcelItem>) => (
        <a
          href={row.data.url}
          target="_blank"
          className="parcel-modal-ellipsis" rel="noreferrer"
        >
          {row.data.name}
        </a>
      ),
      fixWidth: true,
      noOverflow: true,
      width: "185px"
    },
    {
      header: t("parcelModalWindowAttachmentsTable.hscode"),
      accessor: ({ data }) => data.hsCodeRu || data.hsCodeUe || data.hsCodeIs,
      fixWidth: true,
      noOverflow: true,
      type: TableColumnType.TERTIARY,
      width: "110px"
    },
    {
      header: t("parcelModalWindowAttachmentsTable.quantity"),
      accessor: (row: TableRowType<ParcelItem>) => row.data.quantity,
      alignment: "center",
      fixWidth: true,
      noOverflow: true,
      type: TableColumnType.TERTIARY,
      width: "80px"
    },
    {
      header: t("parcelModalWindowAttachmentsTable.cost"),
      accessor: (row: TableRowType<ParcelItem>) => (
          <Text
              color={TextColor.PRIMARY}
              size={TextSize.S}
          >
            {formatSumFromObject(row.data?.declaredValue)}
          </Text>
      ),
      alignment: "right",
      fixWidth: true,
      noOverflow: true,
      width: "120px"
    },
  ];

  if (query.isFetching) {
    return (
      <>
        <div
          className={cn(
            "parcel-modal-title",
            isReturn && "parcel-modal-title--return",
          )}
        />
        <div className="parcel-modal__body">
          <div className="parcel-modal__body-inner">
            <Row>
              <Column phone={12}>
                <div className="parcel-modal__panel parcel-modal__panel--no-border">
                  <Loader size={"s"} />
                </div>
              </Column>
            </Row>
          </div>
        </div>
      </>
    );
  }

  if (query.isError) {
    return (
      <>
        <div
          className={cn(
            "parcel-modal-title",
            isReturn && "parcel-modal-title--return",
          )}
        />
        <div className="parcel-modal__body">
          <div className="parcel-modal__body-inner">
            <Row>
              <Column phone={12}>
                <div className="parcel-modal__panel parcel-modal__panel--no-border">
                  <Text
                    type={TextType.SPAN}
                    size={TextSize.M}
                    color={TextColor.ERROR}
                  >
                    {query.error.message}
                  </Text>
                </div>
              </Column>
            </Row>
          </div>
        </div>
      </>
    );
  }

  const deliveryTrackNumber = query?.data?.returnDeliveryInfo?.deliveryTrackNumber ?? "";
  const deliveryServiceName = query?.data?.returnDeliveryInfo?.deliveryServiceName ?? "";
  const singleNumber = query?.data?.externalId === query?.data?.trackNumber;

  return (
    <>
      <div
        className={cn(
          "parcel-modal-title",
          isReturn && "parcel-modal-title--return",
        )}
      >
        <div className={cn(singleNumber && "parcel-modal-number--single")}>
          <Text type={TextType.H1} color={TextColor.WHITE}>
            {query?.data?.trackNumber}
          </Text>

          {!singleNumber && (
            <Text type={TextType.SPAN} size={TextSize.M} color={TextColor.WHITE}>
              {query?.data?.externalId}
            </Text>
          )}
        </div>

        <div className="parcel-modal-title__status">
          {isReturn ? (
            <>
              <img src={ReturnIcon} alt="icon" />
              {t("parcelModalWindow.statusReturn")}
            </>
          ) : (
            <>
              <img src={RocketIcon} alt="icon" />
              {t("parcelModalWindow.statusSend")}
            </>
          )}
        </div>
      </div>
      <div className="parcel-modal__body">
        <div className="parcel-modal__body-inner">
          <Row>
            <Column phone={12}>
              <div className="parcel-modal__panel-info">
                <Text
                    type={TextType.H3}
                    color={TextColor.PRIMARY}
                    className="parcel-modal-pickup__title"
                >
                  {t("parcelModalWindow.parcel")}
                </Text>
                <div className="parcel-modal__panel-info-inner">
                  <div className="parcel-modal__panel-info-item"/>
                  <div className="parcel-modal__panel-info-item">
                    <span className="parcel-modal__panel-info-item-title">
                      {t("parcelModalWindow.length")}
                    </span>
                  </div>
                  <div className="parcel-modal__panel-info-item">
                    <span className="parcel-modal__panel-info-item-title">
                      {t("parcelModalWindow.width")}
                    </span>
                  </div>
                  <div className="parcel-modal__panel-info-item">
                    <span className="parcel-modal__panel-info-item-title">
                      {t("parcelModalWindow.height")}
                    </span>
                  </div>
                  <div className="parcel-modal__panel-info-item">
                    <span className="parcel-modal__panel-info-item-title">
                      {t("parcelModalWindow.weight")}
                    </span>
                  </div>
                  <div className="parcel-modal__panel-info-item"/>
                  <div className="parcel-modal__panel-info-item">
                    <span className="parcel-modal__panel-info-item-title">
                      {t("parcelModalWindow.postageCost")}
                    </span>
                  </div>
                </div>
                <div className="parcel-modal__panel-info-inner">
                  <div className="parcel-modal__panel-info-item">
                    <span className="parcel-modal__panel-info-item-point">
                      {t('parcelModalWindow.points.actual')}
                    </span>
                  </div>
                  <div className="parcel-modal__panel-info-item">
                    <span className="parcel-modal__panel-info-item-value">
                      {query?.data!.actualDimensions ? formatNumber(query?.data!.actualDimensions.length.value) ?? '' : '—'}
                    </span>
                  </div>
                  <div className="parcel-modal__panel-info-item">
                    <span className="parcel-modal__panel-info-item-value">
                      {query?.data!.actualDimensions ? formatNumber(query?.data!.actualDimensions.width.value) ?? '' : '—'}
                    </span>
                  </div>
                  <div className="parcel-modal__panel-info-item">
                    <span className="parcel-modal__panel-info-item-value">
                      {query?.data!.actualDimensions ? formatNumber(query?.data!.actualDimensions.height.value) ?? '' : '—'}
                    </span>
                  </div>
                  <div className="parcel-modal__panel-info-item">
                    <span className="parcel-modal__panel-info-item-value">
                      {query?.data!.actualWeight ? formatNumber(query?.data!.actualWeight.value) ?? '' : '—'}
                    </span>
                  </div>
                  <div className="parcel-modal__panel-info-item"/>
                  <div className="parcel-modal__panel-info-item">
                    <span className="parcel-modal__panel-info-item-icon">
                      <img src={PriceIcon} alt="Price"/>
                    </span>
                    <span className="parcel-modal__panel-info-item-value">
                      {createSumFromObject(query?.data!.declaredValue)}
                    </span>
                  </div>
                </div>
                <div className="parcel-modal__panel-info-inner">
                  <div className="parcel-modal__panel-info-item">
                    <span className="parcel-modal__panel-info-item-point">
                      {t('parcelModalWindow.points.declared')}
                    </span>
                  </div>
                  <div className="parcel-modal__panel-info-item">
                    <span className="parcel-modal__panel-info-item-value">
                      {formatNumber(query?.data!.dimensions.length.value) ?? ''}
                    </span>
                  </div>
                  <div className="parcel-modal__panel-info-item">
                    <span className="parcel-modal__panel-info-item-value">
                      {formatNumber(query?.data!.dimensions.width.value) ?? ''}
                    </span>
                  </div>
                  <div className="parcel-modal__panel-info-item">
                    <span className="parcel-modal__panel-info-item-value">
                      {formatNumber(query?.data!.dimensions.height.value) ?? ''}
                    </span>
                  </div>
                  <div className="parcel-modal__panel-info-item">
                    <span className="parcel-modal__panel-info-item-value">
                      {formatNumber(query?.data!.weight.value) ?? ''}
                    </span>
                  </div>
                  <div className="parcel-modal__panel-info-item"/>
                  <div className="parcel-modal__panel-info-item"/>
                </div>
              </div>
            </Column>
          </Row>
          <Row>
            <Column phone={12}>
              <div className="parcel-modal__panel parcel-modal__panel--autohight">
                <Text
                  type={TextType.SPAN}
                  size={TextSize.L}
                  color={TextColor.PRIMARY}
                  className="parcel-modal-pickup__title"
                >
                  {query.data?.serviceName || ""}
                </Text>

                {(returnType || returnType === "") ? (
                  <div className="parcel-modal-pickup__description">
                    <Text size={TextSize.S}>
                      {t('parcelModalWindow.description1')}
                    </Text>
                    <Text size={TextSize.S}>
                      {t('parcelModalWindow.description2')}
                    </Text>
                  </div>
                ) : null}

                {isReturn ? (
                  <ToggleButtons
                    value={statusView}
                    onChange={setStatusView}
                    items={[
                      { id: "send", title: t("parcelModalWindow.statuses") },
                      { id: "return", title: t("parcelModalWindow.statusReturn") },
                    ]}
                  />
                ) : null}
                {deliveryTrackNumber && deliveryServiceName ? (
                    <div className="parcel-modal__tracking">
                        <span className="parcel-modal__tracking-title">
                          {t('parcelModalWindow.trackNumber')}
                        </span>
                        <div className="parcel-modal__tracking-info">
                            <span className="parcel-modal__tracking-service">
                              {deliveryServiceName}
                            </span>
                            <CopyTextToClipboard text={deliveryTrackNumber}>
                              {deliveryServiceName === 'CDEK' && (
                                  <a
                                      href={`https://www.cdek.ru/ru/tracking/?order_id=${deliveryTrackNumber}`}
                                      rel="noreferrer"
                                      target="_blank"
                                  >
                                      <span className="parcel-modal__tracking-number">
                                        {deliveryTrackNumber}
                                      </span>
                                  </a>
                              )}
                              {deliveryServiceName === 'STO Express' && (
                                  <a
                                      href={`https://www.sto.cn/pc/service-page/iframe_2_21_${deliveryTrackNumber}`}
                                      rel="noreferrer"
                                      target="_blank"
                                  >
                                      <span className="parcel-modal__tracking-number">
                                        {deliveryTrackNumber}
                                      </span>
                                  </a>
                              )}
                              {deliveryServiceName !== 'CDEK' && deliveryServiceName !== 'STO Express' && (
                                  <span className="parcel-modal__tracking-number">
                                    {deliveryTrackNumber}
                                  </span>
                              )}
                            </CopyTextToClipboard>
                        </div>
                    </div>
                ) : (
                    returnType === 'DeliverToRussia' && (
                        <div className="parcel-modal__tracking">
                            <span className="parcel-modal__tracking-title">
                              {t('parcelModalWindow.trackNumber')}
                            </span>
                            <div className="parcel-modal__tracking-info">
                                <span className="parcel-modal__tracking-service">
                                  {t('parcelModalWindow.message')}
                                </span>
                            </div>
                        </div>
                    )
                )}
                {query?.data?.trackEvents ? (
                  <ParcelModalTimeline
                    items={query?.data?.trackEvents}
                    countryCode={query?.data?.returnDeliveryInfo?.countryCode}
                    tracking={getTracking(statusView, returnType, isProblematic)}
                    estimatedLastMileArrivalTime={query?.data?.estimatedLastMileArrivalTime}
                  />
                ) : null}
              </div>
            </Column>
          </Row>

          {isReturn && imagesQuery?.data?.imageLinks?.length ? (
            <Row>
              <Column phone={12}>
                <div className="parcel-modal__panel">
                  <Text
                    type={TextType.H3}
                    color={TextColor.PRIMARY}
                    className="parcel-modal-pickup__title"
                  >
                    {t("parcelModalWindow.photo")}
                  </Text>

                  <div className="parcel-modal__photos">
                    {imagesQuery?.data?.imageLinks.map((img) => (
                      <a
                        key={img}
                        href={img}
                        className="parcel-modal__photo"
                        target="_blank" rel="noreferrer"
                      >
                        <img src={img} />
                      </a>
                    ))}
                  </div>
                </div>
              </Column>
            </Row>
          ) : null}
          <Row>
            <Column phone={12}>
              <div className="parcel-modal__panel parcel-modal__panel--no-border">
                <Text
                  type={TextType.H3}
                  color={TextColor.PRIMARY}
                  className="parcel-modal-pickup__title"
                >
                  {t("parcelModalWindow.attachments")}
                </Text>

                <Table
                  selectable={false}
                  data={query?.data?.items || ([] as ParcelItem[])}
                  columns={columns}
                  className="parcel-modal-attachments-table"
                />
              </div>
            </Column>
          </Row>
        </div>
      </div>
    </>
  );
};

export default ParcelModalWindow;
